import { Box, useTheme } from '@mui/material';
import React from 'react';

import { LibroTheme } from '../../theme/types';
import Card from '../Card';
import CardContent from '../Card/CardContent';
import CardRow from '../Card/CardRow';
import Container from '../Container';

export const loadingParagraphCID = 'CID-LoadingParagraph';
export const loadingButtonCID = 'CID-LoadingButton';

export const loadingBackground = (theme: LibroTheme) => ({
  animationDuration: '1s',
  animationFillMode: 'forwards',
  animationIterationCount: 'infinite',
  animationName: 'loadingBackgroundAnimation',
  animationTimingFunction: 'linear',
  backgroundImage: `linear-gradient(to right, ${theme.palette.transparent.dark} 8%, ${theme.palette.transparent.midDark} 18%, ${theme.palette.transparent.dark} 33%)`,
  backgroundSize: 400,
  borderRadius: theme.shape.borderRadius,
  color: 'transparent',
  position: 'relative',
});

export const LoadingInline = (): JSX.Element => {
  const theme = useTheme();

  return (
    <Box
      className={loadingParagraphCID}
      sx={{
        ...loadingBackground(theme),
        height: '1em',
        marginBottom: '1em',
        display: 'inline-flex',
        width: '5em',
      }}
      title="Laden"
    />
  );
};

const LoadingParagraph = () => {
  const theme = useTheme();

  return (
    <Box
      className={loadingParagraphCID}
      sx={{ ...loadingBackground(theme), height: '1em', marginBottom: '1em' }}
      title="Laden"
    />
  );
};

export const LoadingButton = (): JSX.Element => {
  const theme = useTheme();

  return (
    <Box
      className={loadingButtonCID}
      sx={{
        ...loadingBackground(theme),
        flexGrow: 1,
        height: '1.5em',
        margin: '1em',
      }}
      title="Laden"
    />
  );
};

export const LoadingCard = (): JSX.Element => {
  const theme = useTheme();

  return (
    <Card>
      <CardContent>
        <Box
          data-testid="loading-card"
          sx={{
            ...loadingBackground(theme),
            height: '2em',
            marginBottom: '1em',
            marginTop: '.5em',
            width: '80%',
          }}
        />
        <LoadingParagraph />
        <LoadingParagraph />
        <Box
          sx={{
            ...loadingBackground(theme),
            height: '1em',
            marginBottom: '1em',
            width: '70%',
          }}
        />
      </CardContent>
    </Card>
  );
};

export interface LoadingCardFixedProps {
  fill?: boolean;
}

export const LoadingDetail = (): JSX.Element => {
  const theme = useTheme();

  return (
    <Box
      className={loadingParagraphCID}
      sx={{
        ...loadingBackground(theme),
        alignSelf: 'center',
        display: 'inline-block',
        height: '16px',
        margin: 0,
        width: '5em',
      }}
      title="Laden"
    />
  );
};

export const LoadingCardFloat = (): JSX.Element => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...loadingBackground(theme),
        borderRadius: '999px !important',
        display: 'inline-block',
        height: '2em',
        verticalAlign: 'middle',
        width: '2em',
      }}
      title="Laden"
    />
  );
};

export const LoadingRow = (): JSX.Element => (
  <CardRow>
    <CardContent>
      <LoadingParagraph />
    </CardContent>
  </CardRow>
);

// Like a LoadingRow, but with a backdrop
export const LoadingCardRowAppendix = (): JSX.Element => (
  <CardRow backdrop>
    <CardContent>
      <LoadingParagraph />
    </CardContent>
  </CardRow>
);

export const LoadingCardContent = (): JSX.Element => {
  const theme = useTheme();

  return (
    <React.Fragment>
      <Box
        sx={{
          ...loadingBackground(theme),
          height: '2em',
          marginBottom: '1em',
          marginTop: '.5em',
          width: '80%',
        }}
        title="Laden"
      />
      <LoadingParagraph />
      <LoadingParagraph />
    </React.Fragment>
  );
};

/**
 * Full page loader, same JSX/HTML as the one that shows when the app is loading
 *  @see {@link /app/spinner}
 *  @return {void}
 */
export const LoadingPage = (): JSX.Element => (
  <Container>
    <LoadingCard />
  </Container>
);

export const LoadingSelect: React.FC<{ style: any }> = ({ style }) => {
  const theme = useTheme();

  return (
    <Box
      style={style}
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: 42,
        padding: '.5em',
        width: '100%',
      }}
      title="Laden"
    >
      <Box
        className={loadingParagraphCID}
        sx={{
          ...loadingBackground(theme),
          height: '1em',
          marginBottom: '1em',
          width: '100%',
        }}
      />
    </Box>
  );
};
